import { api } from '@goaltree/backend/convex/_generated/api'
import { cn } from '@goaltree/ui/lib/utils'
import { useRouterState } from '@tanstack/react-router'
import { useQuery } from 'convex/react'
import { X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useIsPro } from '@/hooks/use-is-pro'
import {
  armSecondBrainDumpTip,
  brainDumpTipPending,
  dismissBrainDumpTip,
  markBrainDumpUsed,
  secondBrainDumpTipDue,
} from '@/lib/first-run'
import { useCaptureStore } from '@/store/capture.store'

// Hint at Brain dump, shown on Today right after onboarding and once more a week
// later if Brain dump still hasn't been opened.
// `placement`: above the + button's menu chevron (phones: the tab bar's centre is
// the coach), or below the sidebar button (the sidebar scroller would clip anything beside it).
export function BrainDumpTip({ placement }: { placement: 'fab' | 'below' }) {
  const { t } = useTranslation()
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const captureOpen = useCaptureStore((s) => s.open)
  const { tier } = useIsPro()
  const [pending, setPending] = useState(brainDumpTipPending)
  const prefs = useQuery(api.preferences.getMyPreferences) as
    | { onboardingCompletedAt?: number }
    | null
    | undefined
  const secondDue =
    !pending && pathname === '/today' && secondBrainDumpTipDue(prefs?.onboardingCompletedAt)
  // Used on another device counts too (the local flag is per device).
  const usage = useQuery(api.usage.getMyUsage, secondDue ? {} : 'skip')

  useEffect(() => {
    if (!secondDue || usage === undefined) return
    if ((usage.features?.brainDump.used ?? 0) > 0) return markBrainDumpUsed()
    armSecondBrainDumpTip()
    setPending(true)
  }, [secondDue, usage])

  const dismiss = () => {
    dismissBrainDumpTip()
    setPending(false)
  }

  useEffect(() => {
    if (captureOpen && pending) dismiss()
  }, [captureOpen])

  if (!pending || pathname !== '/today') return null
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation()
        dismiss()
      }}
      className={cn(
        'absolute z-50 flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-lg bg-foreground py-1.5 pr-2 pl-3 text-xs font-bold text-background shadow-lg animate-in fade-in',
        placement === 'fab' ? 'right-0 bottom-full mb-3' : 'top-full left-0 mt-2',
      )}
    >
      {placement === 'fab'
        ? t(tier === 'trial' ? 'voice.brainDumpTipMenuTrial' : 'voice.brainDumpTipMenu')
        : t(tier === 'trial' ? 'voice.brainDumpTipTrial' : 'voice.brainDumpTip')}
      <X className="h-3 w-3" strokeWidth={2.5} />
      <span
        aria-hidden
        className={cn(
          'absolute h-2 w-2 rotate-45 bg-foreground',
          placement === 'fab' ? 'top-full right-3.5 -mt-1' : 'bottom-full left-4 -mb-1',
        )}
      />
    </button>
  )
}
